precision mediump float;
precision mediump sampler2DArray;

uniform int planeCount;
uniform float time;
uniform float transitionProgress;
uniform float curlFactor;
uniform sampler2DArray map;
uniform int mapDepth;

in vec2 vUv;
flat in int vPlaneIndex;
flat in uint vInstanceIndex;

out vec4 fragColor;

//
// Description : Array and textureless GLSL 2D/3D/4D simplex
//               noise functions.
//      Author : Ian McEwan, Ashima Arts.
//  Maintainer : ijm
//     Lastmod : 20110822 (ijm)
//     License : Copyright (C) 2011 Ashima Arts. All rights reserved.
//               Distributed under the MIT License. See LICENSE file.
//               https://github.com/ashima/webgl-noise
//

vec3 mod289(vec3 x) {
   return x - floor(x * (1.0 / 289.0)) * 289.0;
}

vec4 mod289(vec4 x) {
   return x - floor(x * (1.0 / 289.0)) * 289.0;
}

vec4 permute(vec4 x) {
   return mod289(((x * 34.0) + 1.0) * x);
}

vec4 taylorInvSqrt(vec4 r) {
   return 1.79284291400159 - 0.85373472095314 * r;
}

float snoise(vec3 v) {
   const vec2 C = vec2(1.0 / 6.0, 1.0 / 3.0);
   const vec4 D = vec4(0.0, 0.5, 1.0, 2.0);

// First corner
   vec3 i = floor(v + dot(v, C.yyy));
   vec3 x0 = v - i + dot(i, C.xxx);

// Other corners
   vec3 g = step(x0.yzx, x0.xyz);
   vec3 l = 1.0 - g;
   vec3 i1 = min(g.xyz, l.zxy);
   vec3 i2 = max(g.xyz, l.zxy);

   vec3 x1 = x0 - i1 + C.xxx;
   vec3 x2 = x0 - i2 + C.yyy;
   vec3 x3 = x0 - D.yyy;

// Permutations
   i = mod289(i);
   vec4 p = permute(permute(permute(i.z + vec4(0.0, i1.z, i2.z, 1.0)) + i.y + vec4(0.0, i1.y, i2.y, 1.0)) + i.x + vec4(0.0, i1.x, i2.x, 1.0));

// Gradients: 7x7 points over a square, mapped onto an octahedron.
// The ring size 17*17 = 289 is close to a multiple of 49 (49*6 = 294)
   float n_ = 0.142857142857; // 1.0/7.0
   vec3 ns = n_ * D.wyz - D.xzx;

   vec4 j = p - 49.0 * floor(p * ns.z * ns.z);  //  mod(p,7*7)

   vec4 x_ = floor(j * ns.z);
   vec4 y_ = floor(j - 7.0 * x_);    // mod(j,N)

   vec4 x = x_ * ns.x + ns.yyyy;
   vec4 y = y_ * ns.x + ns.yyyy;
   vec4 h = 1.0 - abs(x) - abs(y);

   vec4 b0 = vec4(x.xy, y.xy);
   vec4 b1 = vec4(x.zw, y.zw);

   vec4 s0 = floor(b0) * 2.0 + 1.0;
   vec4 s1 = floor(b1) * 2.0 + 1.0;
   vec4 sh = -step(h, vec4(0.0));

   vec4 a0 = b0.xzyw + s0.xzyw * sh.xxyy;
   vec4 a1 = b1.xzyw + s1.xzyw * sh.zzww;

   vec3 p0 = vec3(a0.xy, h.x);
   vec3 p1 = vec3(a0.zw, h.y);
   vec3 p2 = vec3(a1.xy, h.z);
   vec3 p3 = vec3(a1.zw, h.w);

//Normalise gradients
   vec4 norm = taylorInvSqrt(vec4(dot(p0, p0), dot(p1, p1), dot(p2, p2), dot(p3, p3)));
   p0 *= norm.x;
   p1 *= norm.y;
   p2 *= norm.z;
   p3 *= norm.w;

// Mix final noise value
   vec4 m = max(0.6 - vec4(dot(x0, x0), dot(x1, x1), dot(x2, x2), dot(x3, x3)), 0.0);
   m = m * m;
   return 42.0 * dot(m * m, vec4(dot(p0, x0), dot(p1, x1), dot(p2, x2), dot(p3, x3)));
}

//
// Description : Array and textureless GLSL 2D/3D/4D simplex
//               noise functions.
//      Author : cabbibo.
//     License : Distributed under the BSD License.
//               https://github.com/cabbibo/glsl-curl-noise/
//

vec3 snoiseVec3(vec3 x) {

   float s = snoise(vec3(x));
   float s1 = snoise(vec3(x.y - 19.1, x.z + 33.4, x.x + 47.2));
   float s2 = snoise(vec3(x.z + 74.2, x.x - 124.5, x.y + 99.4));
   vec3 c = vec3(s, s1, s2);
   return c;

}

vec3 curlNoise(vec3 p) {

   const float e = .1;
   vec3 dx = vec3(e, 0.0, 0.0);
   vec3 dy = vec3(0.0, e, 0.0);
   vec3 dz = vec3(0.0, 0.0, e);

   vec3 p_x0 = snoiseVec3(p - dx);
   vec3 p_x1 = snoiseVec3(p + dx);
   vec3 p_y0 = snoiseVec3(p - dy);
   vec3 p_y1 = snoiseVec3(p + dy);
   vec3 p_z0 = snoiseVec3(p - dz);
   vec3 p_z1 = snoiseVec3(p + dz);

   float x = p_y1.z - p_y0.z - p_z1.y + p_z0.y;
   float y = p_z1.x - p_z0.x - p_x1.z + p_x0.z;
   float z = p_x1.y - p_x0.y - p_y1.x + p_y0.x;

   const float divisor = 1.0 / (2.0 * e);
   return normalize(vec3(x, y, z) * divisor);
}

float random(float x) {
   return fract(sin(dot(vec2(x, 12.9898), vec2(78.233, 157.99))) * 43758.5453);
}

int getRandomTexture() {
    float randomValue = random(float(vInstanceIndex));
    int randomInt = int(randomValue * float(mapDepth));
    return randomInt;
}

void main() {
   vec2 uv = vUv;

   // fragColor = vec4(0, 0, 0, 0);

   float seed = vUv.x * vUv.y;
   float dRnd = random(time + seed + 2.);
   int planeIndex = int(vPlaneIndex);
   float fPlaneIndex = float(planeIndex);

   if(planeIndex == 0) {
      if(dRnd < transitionProgress)
         discard;
   }

   if(planeIndex != 0 && transitionProgress == 0.) {
      discard;
   }

   if (transitionProgress == 1.) {
      discard;
   }

   if (transitionProgress > 0.) {
      float uvFactor = .8;
      float instanceIndex = float(vInstanceIndex);
      vec3 distortionBase = vec3(uv.x * uvFactor + time * 0.1 + (instanceIndex + fPlaneIndex) * 100., uv.y * uvFactor + time * 0.1 + (instanceIndex + fPlaneIndex) * 100., (uv.x + uv.y) * uvFactor + (instanceIndex + fPlaneIndex) * 100.);
      vec3 distortion = vec3(uv.x, uv.y, 1.) * curlNoise(distortionBase);

      uv.x += distortion.x * curlFactor * transitionProgress;
      uv.y += distortion.y * curlFactor * transitionProgress;

      if(planeIndex != 0) {
         float dRnd2 = random(time + seed + 3.);
         if(dRnd >= transitionProgress)
            discard;
         if (dRnd2 < transitionProgress)
            discard;

         float angleUnit = 3.141592653 / float(planeCount);
         float particleAngle = atan(distortion.z - .5, distortion.x - .5);

         if(particleAngle < 0.0) {
            particleAngle += 2.0 * 3.141592653;
         }

         int closestPlane = abs(int(round(particleAngle / angleUnit)) - planeCount);

         if(planeIndex != closestPlane)
            discard;
      }
   }

   vec4 textureColor = texture(map, vec3(uv, getRandomTexture()));

   if (textureColor.a < 0.5) {
      discard;
   }

   fragColor = vec4(textureColor.rgb, textureColor.a);
}